import React from "react";
import {Grid} from "@mui/material";
import {Desk, Floor, INeighbourhood, UNASSIGNED_NEIGHBOURHOOD_ID} from "../../api/Model";
import {ClearFilterButton} from "../../components/filter-fields/ClearFilterButton";
import {NeighbourhoodsFilter} from "../../components/filter-fields/NeighbourhoodsFilter";
import {StringEnumFilter} from "../../components/filter-fields/StringEnumFilter";
import {TextSearchFilter} from "../../components/filter-fields/TextSearchFilter";
import {filterSerializer} from "../../util/FilterSerializer";
import {SetState} from "../../util/FormHandlers";
import {naturalCmpOptions, stringCmp} from "../../util/SortUtil";
import {andFilter, applyFilter, filterChoice, filterText, filterValue, matchMultiSelect} from "../../util/FilterUtil";
import {idFn} from "../../util/FunctionUtil";
import {DeskModel} from "./DeskModel";
import {DateRangeFilter, DateRangeOption, DateRangeOptions} from "../../components/filter-fields/DateRangeFilter";
import {isLowAttendance} from "../../util/visit/LowAttendance";

type DeskAssignmentFilter = '' | 'assigned' | 'shared';
type DeskProblemFilter = '' | 'inactive-employee' | 'not-resident' | 'multiple-desks' | 'neighbourhood-mismatch' | 'low-attendance';

export interface DeskFilter {
    dateRange: DateRangeOption;
    search: string;
    floorId: string;
    assignment: DeskAssignmentFilter;
    problem: DeskProblemFilter;
    neighbourhoods: INeighbourhood[];
}

export const NO_FLOOR_FILTER = 'no-floor';

export const emptyDeskFilter: DeskFilter = {
    dateRange: DateRangeOptions.lastThreeMonths,
    search: '',
    floorId: '',
    assignment: '',
    problem: '',
    neighbourhoods: [],
};

export const deskFilterSerializer = (neighbourhoods: INeighbourhood[]) => filterSerializer(emptyDeskFilter, b => {
    b.dateRange('dateRange');
    b.str('search');
    b.str('floorId');
    b.str('assignment');
    b.str('problem');
    b.neighbourhoods('neighbourhoods', neighbourhoods);
});

const assignmentOptions: { value: DeskAssignmentFilter, label: string }[] = [
    {value: '', label: 'All Desks'},
    {value: 'assigned', label: 'Assigned'},
    {value: 'shared', label: 'Shared'},
];

const problemOptions: { value: DeskProblemFilter, label: string }[] = [
    {value: '', label: 'All Desks'},
    {value: 'inactive-employee', label: 'Employee is inactive'},
    {value: 'not-resident', label: 'Employee is not a resident'},
    {value: 'multiple-desks', label: 'More than one desk per person'},
    {value: 'neighbourhood-mismatch', label: 'Desk neighbourhood doesn\'t match employee\'s neighbourhood'},
    {value: 'low-attendance', label: 'Employee visited on less than 30% of workdays'},
];

export function filterDesks(
    desks: Desk[],
    filter: DeskFilter,
    deskModel: DeskModel,
): Desk[] {
    return applyFilter(desks, andFilter(
        filterValue(
            filter.floorId ? floorId => filter.floorId === NO_FLOOR_FILTER
                    ? !floorId
                    : floorId === filter.floorId
                : null,
            desk => desk.floorId,
        ),
        filterChoice(filter.assignment, {
            '': undefined,
            assigned: desk => !!deskModel.employee(desk),
            shared: desk => !deskModel.employee(desk),
        }),
        filterChoice(filter.problem, {
            '': undefined,
            'inactive-employee': desk => deskModel.employee(desk)?.deactivated === true,
            'not-resident': desk => !deskModel.isResident(desk),
            'multiple-desks': desk => deskModel.isDuplicatedDesk(desk),
            'neighbourhood-mismatch': desk => deskModel.neighbourhoodMismatch(desk) != null,
            'low-attendance': desk => isLowAttendance(deskModel.employeeVisitStats(desk)?.stats),
        }),
        filterValue(
            matchMultiSelect(filter.neighbourhoods.map(idFn)),
            desk => deskModel.neighbourhood(desk)?.id ?? UNASSIGNED_NEIGHBOURHOOD_ID,
        ),
        filterText(
            filter.search,
            desk => [
                desk.name,
                desk.roomName,
                deskModel.employee(desk)?.fullName,
                deskModel.floor(desk)?.name,
                deskModel.neighbourhood(desk)?.name,
            ],
        )
    ));
}

export function DeskTableFilter({filter, setFilter, floors, neighbourhoods}: {
    filter: DeskFilter;
    setFilter: SetState<DeskFilter>;
    floors: Floor[];
    neighbourhoods: INeighbourhood[];
}) {
    const floorOptions = [
        {value: '', label: 'All Floors'},
        ...floors
            .map(floor => ({value: floor.id, label: floor.name}))
            .sort(stringCmp('label', true, naturalCmpOptions)),
        {value: NO_FLOOR_FILTER, label: 'No Floor'},
    ];

    return (
        <Grid container spacing={2} alignItems="center">
            <DateRangeFilter filter={filter} setFilter={setFilter} property="dateRange"/>
            <StringEnumFilter label="Floor" filter={filter} setFilter={setFilter} property="floorId"
                              options={floorOptions}/>
            <NeighbourhoodsFilter filter={filter} setFilter={setFilter} allNeighbourhoods={neighbourhoods}/>
            <StringEnumFilter label="Assignment" filter={filter} setFilter={setFilter} property="assignment"
                              options={assignmentOptions}/>
            <StringEnumFilter label="Problems" filter={filter} setFilter={setFilter} property="problem"
                              options={problemOptions}/>
            <TextSearchFilter filter={filter} setFilter={setFilter} property="search"/>
            <ClearFilterButton emptyFilter={emptyDeskFilter} setFilter={setFilter}/>
        </Grid>
    );
}
